import {isReadOSTurnTerminalEvent,isNativeTurnLifecycleEvent,nativeTurnLifecycleIdentity,type CodexNotification,type ReadOSTurnTerminalEvent,type NativeTurnLifecycleEvent} from '@reados/contracts';

export interface TurnTerminalHistory {ownerThreadId:string;events:ReadOSTurnTerminalEvent[]}

/** Keep terminal receipts outside the bounded transient event window. An older
 * history request cannot reopen a turn or replace a terminal already received
 * live. Native thread and turn identities both participate in the join. */
export function mergeTurnTerminalHistory(current:TurnTerminalHistory|null,incoming:readonly CodexNotification[],ownerThreadId:string):TurnTerminalHistory {
  const merged=new Map<string,ReadOSTurnTerminalEvent>();
  const previous=current?.ownerThreadId===ownerThreadId?current.events:[];
  for(const event of [...previous,...incoming]){
    if(!isReadOSTurnTerminalEvent(event)||event.params.ownerThreadId!==ownerThreadId)continue;
    const key=JSON.stringify([event.params.threadId,event.params.turnId]);
    const existing=merged.get(key);
    // The earliest verified observation is immutable for presentation purposes.
    if(!existing||event.params.completedAtMilliseconds<existing.params.completedAtMilliseconds)merged.set(key,event);
  }
  return {ownerThreadId,events:[...merged.values()]};
}

export function turnTerminalEventsForThread(history:TurnTerminalHistory|null,ownerThreadId:string|null):ReadOSTurnTerminalEvent[] {
  return history?.ownerThreadId===ownerThreadId?history.events:[];
}

export interface TurnLifecycleHistory {ownerThreadId:string;events:NativeTurnLifecycleEvent[]}

/** Preserve the original lifecycle payloads independently of transient events.
 * A history response fills gaps without replacing a newer same-method live
 * payload. The Web owner is external to unchanged native/fork identities. */
export function mergeTurnLifecycleHistory(current:TurnLifecycleHistory|null,incoming:readonly CodexNotification[],ownerThreadId:string,source:'history'|'live'='live'):TurnLifecycleHistory {
  const previous=current?.ownerThreadId===ownerThreadId?current.events:[];
  const merged=new Map<string,NativeTurnLifecycleEvent>(),order=new Map<string,number>();
  const batches=source==='history'?[incoming,previous]:[previous,incoming];
  for(const events of batches)for(const event of events){
    if(!isNativeTurnLifecycleEvent(event))continue;
    const identity=nativeTurnLifecycleIdentity(event),turn=JSON.stringify([identity.threadId,identity.turnId]);
    if(!order.has(turn))order.set(turn,order.size);
    merged.set(JSON.stringify([identity.threadId,identity.turnId,event.method]),event);
  }
  const events=[...merged.values()].sort((a,b)=>{
    const ai=nativeTurnLifecycleIdentity(a),bi=nativeTurnLifecycleIdentity(b),ak=JSON.stringify([ai.threadId,ai.turnId]),bk=JSON.stringify([bi.threadId,bi.turnId]);
    return ak===bk?Number(a.method!=='turn/started')-Number(b.method!=='turn/started'):order.get(ak)!-order.get(bk)!;
  });
  return {ownerThreadId,events};
}

export function turnLifecycleEventsForThread(history:TurnLifecycleHistory|null,ownerThreadId:string|null):NativeTurnLifecycleEvent[] {
  return history?.ownerThreadId===ownerThreadId?history.events:[];
}
